import type { ReactNode } from "react";
import type { TFunction } from "i18next";
import type { SvgIconComponent } from "@mui/icons-material";
import { ApplicationNavIcon } from "@components/icons/sidebar/ApplicationNavIcon";
import { AdmissionsNavIcon } from "@components/icons/sidebar/AdmissionsNavIcon";
import { EChallanStatusNavIcon } from "@components/icons/sidebar/EChallanStatusNavIcon";
import { EChallanSummaryNavIcon } from "@components/icons/sidebar/EChallanSummaryNavIcon";
import { FacultyNavIcon } from "@components/icons/sidebar/FacultyNavIcon";
import { RegistrationNavIcon } from "@components/icons/sidebar/RegistrationNavIcon";
import { TrackApplicationNavIcon } from "@components/icons/sidebar/TrackApplicationNavIcon";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import { sidebarMenuIcon } from "../sidebarMenuIcon";
import {
	REGISTRATION_GROUP_MENU_KEY,
	INSTITUTE_STATS_DASHBOARD_MENU_KEY,
	ACADEMIC_CALENDAR_MENU_KEY,
	DASHBOARD_MENU_KEY,
	APPLICATIONS_MENU_KEY,
	ADMISSIONS_LIST_MENU_KEY,
	E_CHALLAN_STATUS_MENU_KEY,
	E_CHALLAN_SUMMARY_MENU_KEY,
	TRACK_APPLICATION_MENU_KEY,
	OPEN_ADMISSION_MENU_KEY,
	MANAGE_SESSIONS_MENU_KEY,
	FACULTY_MENU_KEY,
	ATTENDANCE_MENU_KEY,
	STUDENT_ATTENDANCE_MENU_KEY,
	FACULTY_ATTENDANCE_MENU_KEY,
	PROMOTION_MENU_KEY,
	DROPOUT_MENU_KEY,
	MIGRATION_REQUESTS_MENU_KEY,
	EMPLOYMENT_STATUS_MENU_KEY,
	MANAGE_OJT_MENU_KEY,
	RESULT_MANAGEMENT_MENU_KEY,
	SESSION_STUDENTS_MENU_KEY,
	STUDENT_CERTIFICATES_MENU_KEY,
} from "./menu.keys";

/** Leaf menu item (no nested children) */
export interface SidebarMenuLeaf {
	key: string;
	label: string;
	icon: ReactNode;
	route?: string;
}

/** Child item: can be a link or a group with grandchildren */
export interface SidebarMenuItemChild {
	key: string;
	label: string;
	icon: ReactNode;
	route?: string;
	children?: SidebarMenuLeaf[];
}

/** Top-level sidebar menu item */
export interface SidebarMenuItem {
	key: string;
	label: string;
	icon: ReactNode;
	route?: string;
	children?: SidebarMenuItemChild[];
}

const menuIcon = (Icon: SvgIconComponent) => sidebarMenuIcon(<Icon />);

export const getSidebarMenuConfig = (t: TFunction): SidebarMenuItem[] => {
	return [
		{
			key: INSTITUTE_STATS_DASHBOARD_MENU_KEY,
			label: t("layout.sidebar.menuName.instituteStatsDashboard"),
			icon: menuIcon(WidgetsOutlinedIcon),
			route: BROWSER_ROUTES.INSTITUTE_STATS_DASHBOARD,
		},
		{
			key: ACADEMIC_CALENDAR_MENU_KEY,
			label: t("layout.sidebar.menuName.academicCalendar"),
			icon: menuIcon(DateRangeOutlinedIcon),
			route: BROWSER_ROUTES.ACADEMIC_CALENDAR,
		},
		{
			key: REGISTRATION_GROUP_MENU_KEY,
			label: t("layout.sidebar.menuName.register"),
			icon: sidebarMenuIcon(<RegistrationNavIcon />),
			children: [
				{
					key: DASHBOARD_MENU_KEY,
					label: t("layout.sidebar.menuName.dashboard"),
					icon: sidebarMenuIcon(<ApplicationNavIcon />),
					route: BROWSER_ROUTES.DASHBOARD,
				},
				{
					key: TRACK_APPLICATION_MENU_KEY,
					label: t("layout.sidebar.menuName.trackApplication"),
					icon: sidebarMenuIcon(<TrackApplicationNavIcon />),
					route: BROWSER_ROUTES.TRACK_APPLICATION,
				},
				{
					key: E_CHALLAN_STATUS_MENU_KEY,
					label: t("layout.sidebar.menuName.eChallanStatus"),
					icon: sidebarMenuIcon(<EChallanStatusNavIcon />),
					route: BROWSER_ROUTES.E_CHALLAN_STATUS,
				},
				{
					key: E_CHALLAN_SUMMARY_MENU_KEY,
					label: t("layout.sidebar.menuName.eChallanSummary"),
					icon: sidebarMenuIcon(<EChallanSummaryNavIcon />),
					route: BROWSER_ROUTES.E_CHALLAN_SUMMARY,
				},
			],
		},
		{
			key: APPLICATIONS_MENU_KEY,
			label: t("layout.sidebar.menuName.admission"),
			icon: sidebarMenuIcon(<AdmissionsNavIcon />),
			children: [
				{
					key: ADMISSIONS_LIST_MENU_KEY,
					label: t("layout.sidebar.menuName.admissionList"),
					icon: menuIcon(ListAltOutlinedIcon),
					route: BROWSER_ROUTES.ADMISSIONS,
				},
				{
					key: OPEN_ADMISSION_MENU_KEY,
					label: t("layout.sidebar.menuName.openAdmission"),
					icon: menuIcon(PlayCircleOutlineOutlinedIcon),
					route: BROWSER_ROUTES.OPEN_ADMISSION,
				},
				{
					key: MANAGE_SESSIONS_MENU_KEY,
					label: t("layout.sidebar.menuName.manageSessions"),
					icon: menuIcon(EventNoteOutlinedIcon),
					route: BROWSER_ROUTES.MANAGE_SESSIONS,
				},
				{
					key: PROMOTION_MENU_KEY,
					label: t("layout.sidebar.menuName.promotion"),
					icon: menuIcon(TrendingUpOutlinedIcon),
					route: BROWSER_ROUTES.PROMOTION,
				},
				{
					key: DROPOUT_MENU_KEY,
					label: t("layout.sidebar.menuName.dropout"),
					icon: menuIcon(PersonOffOutlinedIcon),
					route: BROWSER_ROUTES.DROPOUT,
				},
				{
					key: MIGRATION_REQUESTS_MENU_KEY,
					label: t("layout.sidebar.menuName.migrationRequests"),
					icon: menuIcon(SwapHorizOutlinedIcon),
					route: BROWSER_ROUTES.MIGRATION_REQUESTS,
				},
				{
					key: EMPLOYMENT_STATUS_MENU_KEY,
					label: t("layout.sidebar.menuName.employmentStatus"),
					icon: menuIcon(WorkOutlineOutlinedIcon),
					route: BROWSER_ROUTES.EMPLOYMENT_STATUS,
				},
				{
					key: MANAGE_OJT_MENU_KEY,
					label: t("layout.sidebar.menuName.manageOjt"),
					icon: menuIcon(AssignmentOutlinedIcon),
					route: BROWSER_ROUTES.MANAGE_OJT,
				},
				{
					key: ATTENDANCE_MENU_KEY,
					label: t("layout.sidebar.menuName.attendance"),
					icon: menuIcon(CalendarMonthOutlinedIcon),
					children: [
						{
							key: STUDENT_ATTENDANCE_MENU_KEY,
							label: t("layout.sidebar.menuName.studentAttendance"),
							icon: menuIcon(PersonOutlineOutlinedIcon),
							route: BROWSER_ROUTES.STUDENT_ATTENDANCE,
						},
						{
							key: FACULTY_ATTENDANCE_MENU_KEY,
							label: t("layout.sidebar.menuName.facultyAttendance"),
							icon: menuIcon(GroupsOutlinedIcon),
							route: BROWSER_ROUTES.FACULTY_ATTENDANCE,
						},
					],
				},
			],
		},
		{
			key: RESULT_MANAGEMENT_MENU_KEY,
			label: t("layout.sidebar.menuName.resultManagement"),
			icon: menuIcon(AssignmentOutlinedIcon),
			route: BROWSER_ROUTES.RESULT_MANAGEMENT,
		},
		{
			key: SESSION_STUDENTS_MENU_KEY,
			label: t("layout.sidebar.menuName.sessionStudents"),
			icon: menuIcon(FactCheckOutlinedIcon),
			route: BROWSER_ROUTES.SESSION_STUDENTS,
		},
		{
			key: STUDENT_CERTIFICATES_MENU_KEY,
			label: t("layout.sidebar.menuName.studentCertificates"),
			icon: menuIcon(WorkspacePremiumOutlinedIcon),
			route: BROWSER_ROUTES.STUDENT_CERTIFICATES,
		},
		{
			key: FACULTY_MENU_KEY,
			label: t("layout.sidebar.menuName.faculty"),
			icon: sidebarMenuIcon(<FacultyNavIcon />),
			route: BROWSER_ROUTES.FACULTY,
		},
	];
};

/**
 * Faculty users only see Student Attendance, Promotion, Dropout, Employment Status, Result Management, Session Students, and Student Certificates.
 * Keep Attendance as the parent tab with Student as the sub-item (same pattern as institute menu).
 */
export const getFacultySidebarMenuConfig = (t: TFunction): SidebarMenuItem[] => {
	return [
		{
			key: ATTENDANCE_MENU_KEY,
			label: t("layout.sidebar.menuName.attendance"),
			icon: menuIcon(CalendarMonthOutlinedIcon),
			children: [
				{
					key: STUDENT_ATTENDANCE_MENU_KEY,
					label: t("layout.sidebar.menuName.studentAttendance"),
					icon: menuIcon(PersonOutlineOutlinedIcon),
					route: BROWSER_ROUTES.STUDENT_ATTENDANCE,
				},
			],
		},
		{
			key: PROMOTION_MENU_KEY,
			label: t("layout.sidebar.menuName.promotion"),
			icon: menuIcon(TrendingUpOutlinedIcon),
			route: BROWSER_ROUTES.PROMOTION,
		},
		{
			key: DROPOUT_MENU_KEY,
			label: t("layout.sidebar.menuName.dropout"),
			icon: menuIcon(PersonOffOutlinedIcon),
			route: BROWSER_ROUTES.DROPOUT,
		},
		{
			key: EMPLOYMENT_STATUS_MENU_KEY,
			label: t("layout.sidebar.menuName.employmentStatus"),
			icon: menuIcon(WorkOutlineOutlinedIcon),
			route: BROWSER_ROUTES.EMPLOYMENT_STATUS,
		},
		{
			key: RESULT_MANAGEMENT_MENU_KEY,
			label: t("layout.sidebar.menuName.resultManagement"),
			icon: menuIcon(AssignmentOutlinedIcon),
			route: BROWSER_ROUTES.RESULT_MANAGEMENT,
		},
		{
			key: SESSION_STUDENTS_MENU_KEY,
			label: t("layout.sidebar.menuName.sessionStudents"),
			icon: menuIcon(FactCheckOutlinedIcon),
			route: BROWSER_ROUTES.SESSION_STUDENTS,
		},
		{
			key: STUDENT_CERTIFICATES_MENU_KEY,
			label: t("layout.sidebar.menuName.studentCertificates"),
			icon: menuIcon(WorkspacePremiumOutlinedIcon),
			route: BROWSER_ROUTES.STUDENT_CERTIFICATES,
		},
	];
};
